import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';

// Generic CSV Export Modal
// Props:
// - show: boolean
// - onHide: function
// - columns: array of column definitions ({ name, selector, cell, exportCell })
// - rows: array of row objects currently displayed in the table
// - filename: string for download filename
// - excludeColumnNames: array of column name strings to exclude from selection (e.g., ['Actions'])
export default function CSVExportModal({ show, onHide, columns = [], rows = [], filename = 'export.csv', excludeColumnNames = ['Actions'], preselectedColumnNames = [] }) {
  const initialSelectableCols = useMemo(() =>
    columns.filter(col => !excludeColumnNames.includes(col.name)),
    [columns, excludeColumnNames]
  );

  const computeDefaultSelection = () => {
    if (preselectedColumnNames && preselectedColumnNames.length > 0) {
      return initialSelectableCols
        .filter(c => preselectedColumnNames.includes(c.name))
        .map(c => c.name);
    }
    return initialSelectableCols.map(c => c.name);
  };

  const [selectedNames, setSelectedNames] = useState(() => computeDefaultSelection());

  useEffect(() => {
    // Reset selection when modal opens or columns change
    setSelectedNames(computeDefaultSelection());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, columns, excludeColumnNames, preselectedColumnNames]);

  const toggleSelection = (name) => {
    setSelectedNames(prev => (
      prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
    ));
  };

  const getCellValue = (col, row) => {
    try {
      if (typeof col.exportCell === 'function') {
        const v = col.exportCell(row);
        return v == null ? '' : String(v);
      }
      if (typeof col.selector === 'function') {
        const v = col.selector(row);
        return v == null ? '' : String(v);
      }
      if (col.sortField && row && Object.prototype.hasOwnProperty.call(row, col.sortField)) {
        const v = row[col.sortField];
        return v == null ? '' : String(v);
      }
      return '';
    } catch (e) {
      return '';
    }
  };

  const selectedCols = useMemo(() => initialSelectableCols.filter(c => selectedNames.includes(c.name)), [initialSelectableCols, selectedNames]);

  const csvText = useMemo(() => {
    if (!rows || rows.length === 0 || selectedCols.length === 0) return '';
    const escapeCSV = (value) => {
      const str = value == null ? '' : String(value);
      if (/[",\n]/.test(str)) {
        return '"' + str.replace(/"/g, '""') + '"';
      }
      return str;
    };
    const headers = selectedCols.map(c => escapeCSV(c.name)).join(',');
    const lines = rows.map(row => selectedCols.map(col => escapeCSV(getCellValue(col, row))).join(','));
    return [headers, ...lines].join('\n');
  }, [rows, selectedCols]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(csvText);
      alert('CSV copied to clipboard');
    } catch (e) {
      alert('Failed to copy CSV');
    }
  };

  const handleDownload = () => {
    const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>CSV Preview</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <div className="d-flex flex-wrap">
            {initialSelectableCols.map(col => (
              <Form.Check
                key={col.name}
                type="checkbox"
                id={`csv-col-${col.name}`}
                className="me-3 mb-2"
                label={col.name}
                checked={selectedNames.includes(col.name)}
                onChange={() => toggleSelection(col.name)}
              />
            ))}
          </div>
        </div>
        <Form.Group className="mb-3">
          <Form.Label>Preview</Form.Label>
          <Form.Control as="textarea" rows={12} value={csvText} readOnly />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCopy}>Copy</Button>
        <Button variant="primary" onClick={handleDownload}>Download CSV</Button>
      </Modal.Footer>
    </Modal>
  );
}